import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Model, Serializer, ElementFactory, settings } from "survey-core";
import * as SurveyCore from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/survey-core.css";
import "surveyjs-widgets/node_modules/nouislider/distribute/nouislider.css";
import nouislider from "surveyjs-widgets/node_modules/nouislider/distribute/nouislider";
// eslint-disable-next-line @typescript-eslint/no-var-requires
import * as widgets from "surveyjs-widgets";
import { usePortal } from "../../context/PortalContext";
import { useUserAuth } from "../../context/AuthContext";
import { defaultTheme } from "./surveyTheme";
import { Loader } from "../common/modals/LoaderModal";
import { LazyLoadingDropdownFields } from "../forms/form_fields/LazyLoadingDropdownFields";
import "../forms/form_fields/DatePickerMultiInput";
import "../forms/form_fields/AddressAutocomplete";
import "../forms/form_fields/postcode_surveyjs";
import "../forms/form_fields/AgeInput";
import {
  handleQuestionRendering,
  onChoicesLazyLoadFunction,
  onUpdateQuestionCssClassesFunction,
  unflattenData,
  ILazyLoadingDropdownField,
  IProps,
  getFlattenedData,
  onGetChoiceDisplayValueFunction,
} from "../../helpers/SurveyJSHelpers";
import {
  buildDraftKey,
  clearSessionDraftFields,
  getDraftUserId,
} from "../../helpers/SessionStorageHelpers";
import { useTokenPrefillHook } from "../../hooks/useTokenPrefillHook";
import { useSurveyRestoreHook } from "../../hooks/useSurveyRestoreHook";
import { useSurveySessionSaverHook } from "../../hooks/useSurveySessionSaverHook";

// surveyjs-widgets v1 calls registerCustomQuestion which doesn't exist in survey-core v2.
// We initialise the widget first (which calls Serializer.addClass to register the metadata),
// then manually register the element with the v2 ElementFactory API.
widgets.nouislider(SurveyCore, nouislider);
settings.titleTags.question = "div";
Serializer.addProperty("nouislider", {
  name: "tooltipTexts:itemvalues",
  category: "slider",
});
Serializer.addProperty("nouislider", {
  name: "tooltipSuffix",
  category: "slider",
});
ElementFactory.Instance.registerElement("nouislider", (name) =>
  Serializer.createClass("nouislider", { name })
);

export interface SurveyJSFormRef {
  saveAndExit: () => void;
  toggleLoader: () => void;
  submitSurvey: () => void;
  getData: () => { [key: string]: any };
  discardDraft: () => void;
  resetToInitialData: () => void;
  /** Validates every page and shows the errors; true when the answers are valid. */
  validate: () => boolean;
}

export const SurveyJSForm = forwardRef<SurveyJSFormRef, IProps>(
  (props, ref) => {
    const {
      pageSchemas,
      onComplete,
      initialData,
      surveyFormId,
      surveyProps = {},
      onPageNext,
      onPagePrev,
      onBeforeComplete,
      surveyTheme,
      wrapperFormCssClass,
      onPageChanging,
      autoskip = false,
      autoAdvanceEnabled = false,
      addCSSClassToQuestions,
      showSaveAndExit,
      onSaveAndExit,
      keepFormVisibleOnComplete = false,
      suppressLoader = false,
      disableCompleteUntilCurrentPageComplete = false,
      surveyErrorTextColor,
      onFirstInteraction,
      clearDraftOnComplete = true,
      disableTokenPrefill = false,
    } = props;

    const surveyContainerRef = useRef<HTMLDivElement>(null);
    const modelRef = useRef<Model | null>(null);
    const sessionDraftKeyRef = useRef<string>("");
    // Clears only this form's own answers from the shared draft.
    const clearOwnDraftFields = () => {
      const names = modelRef.current?.getAllQuestions().map((question) => question.name).filter(Boolean) ?? [];
      clearSessionDraftFields(sessionDraftKeyRef.current, names);
    };
    const [loadingMessage, setLoadingMessage] = useState("Please wait...");
    const surveyContainerId = `surveyContainer-${surveyFormId}`;
    const loaderWrapperId = `loaderWrapper-${surveyFormId}`;
    const surveyContainerStyle = useMemo(
      () =>
        ({
          display: "block",
          ...(surveyErrorTextColor
            ? { "--survey-error-text-color": surveyErrorTextColor }
            : {}),
        } as React.CSSProperties),
      [surveyErrorTextColor]
    );

    const { getPrefillDataFromToken } = usePortal();
    const { user } = useUserAuth();

    const updateCompleteButtonEnabled = useCallback((model: Model) => {
      if (!disableCompleteUntilCurrentPageComplete) return;

      const requiredQuestions = (model.currentPage?.questions ?? []).filter(
        (question: any) => question.isVisible !== false && question.isRequired
      );
      const isAnswered = requiredQuestions.every((question: any) =>
        typeof question.isEmpty === "function"
          ? !question.isEmpty()
          : question.value !== undefined && question.value !== null && question.value !== ""
      );
      const completeAction = model.navigationBar.getActionById("sv-nav-complete");

      if (completeAction) {
        completeAction.enabled = isAnswered;
      }
    }, [disableCompleteUntilCurrentPageComplete]);


    useImperativeHandle(ref, () => ({
      saveAndExit: () => {
        if (!modelRef.current) return;
        clearOwnDraftFields();
        const surveyData = modelRef.current.data;
        const unflattened = unflattenData(surveyData);
        return unflattened;
      },
      toggleLoader: () => {
        if (suppressLoader) return;
        const loaderWrapper = document.getElementById(loaderWrapperId);
        const container = document.getElementById(surveyContainerId);

        if (container) {
          container.style.display =
            container.style.display === "none" ? "block" : "none";
        }
        if (loaderWrapper) {
          loaderWrapper.style.display =
            loaderWrapper.style.display === "none" ? "block" : "none";
        }
      },
      submitSurvey: () => {
        modelRef.current?.tryComplete();
      },
      getData: () => {
        return modelRef.current?.data || {};
      },

      discardDraft: () => {
        clearOwnDraftFields();
      },
      resetToInitialData: () => {
        if (modelRef.current && initialData) {
          const flattenedData = getFlattenedData(initialData ?? {}) || {};
          modelRef.current.data = flattenedData;
          // Reset all question values (SurveyJS way)
          modelRef.current.getAllQuestions().forEach((q) => {
            const name = q.name;
            if (!name) return;
            const value = flattenedData[name];
            q.value = value !== undefined ? value : undefined;
          });
          modelRef.current.render();
        }
      },
      validate: () => modelRef.current?.validate(true, true) ?? false,
    }));

    // Init only once
    if (!modelRef.current) {
      Serializer.addProperty("question", {
        name: "elementCssClass",
        type: "text",
      });

      const lazyFields: ILazyLoadingDropdownField[] =
        LazyLoadingDropdownFields(pageSchemas);

      const surveyData = getFlattenedData(initialData ?? {});

      const combinedSchema = {
        ...surveyProps,
        pages: pageSchemas.map(({ name, title, description, elements, visibleIf }) => ({
          name,
          title,
          description,
          elements,
          visibleIf,
        })),
        showCompletePage: false,
        clearInvisibleValues: "none",
      };

      const model = new Model(combinedSchema);
      model.applyTheme(surveyTheme || defaultTheme);
      model.data = surveyData;

      if (addCSSClassToQuestions) {
        addCSSClassToQuestions.forEach((q) => {
          model
            .getQuestionByName(q.questionName)
            ?.setPropertyValue("elementCssClass", q.cssClass);
        });
      }

      if (showSaveAndExit) {
        model.addNavigationItem({
          id: "sv-nav-save-exit",
          title: "Save & Exit",
          action: () => {
            // Clear session storage before save (security)
            clearOwnDraftFields();
            props.onSaveAndExit?.(unflattenData(model.data));
          },
          css: "sv-action",
          innerCss: "sd-btn btn btn-sm bg-transparent text-decoration-underline p-0 flex-grow-0 m-auto",
        });
      }

      model.onUpdateQuestionCssClasses.add(onUpdateQuestionCssClassesFunction);
      model.onAfterRenderQuestion.add(handleQuestionRendering);
      model.onChoicesLazyLoad.add((_, options) =>
        onChoicesLazyLoadFunction(_, options, lazyFields)
      );
      model.onGetChoiceDisplayValue.add((_, options) =>
        onGetChoiceDisplayValueFunction(_, options, lazyFields)
      );

      model.css = {
        navigation: {
          next: "bg-primary text-white",
          prev: "bg-light border border-secondary text-black",
          // complete: "",
          // start: "",
          // preview: ""
        }
      }

      if (onPageNext || onPagePrev || onPageChanging) {
        model.onCurrentPageChanged.add((sender, options) => {
          const data = unflattenData(sender.data);
          if (options.isNextPage) onPageNext?.(data);
          if (options.isPrevPage) onPagePrev?.(data);
          if (onPageChanging)
            onPageChanging(options.newCurrentPage, options.oldCurrentPage);
          updateCompleteButtonEnabled(sender);
        });
      }

      model.onCompleting.add((sender, options) => {
        if (keepFormVisibleOnComplete) {
          const allow = onBeforeComplete?.(sender.data);
          if (allow === false) {
            options.allow = false;
            return;
          }

          options.allow = false;
          if (clearDraftOnComplete) clearOwnDraftFields();
          setTimeout(() => onComplete?.(unflattenData(sender.data)), 0);
          return;
        }

        const loader = document.getElementById(loaderWrapperId);
        const container = document.getElementById(surveyContainerId);

        if (!suppressLoader && loader && container && loader.style.display === "none") {
          loader.style.display = "block";
          container.style.display = "none";
          options.allow = false;
          setTimeout(() => {
            options.allow = true;
            sender.tryComplete();
          }, 200);
        }
      });

      model.onComplete.add((sender) => {
        if (keepFormVisibleOnComplete) {
          return;
        }

        const data = unflattenData(sender.data);
        const allow = onBeforeComplete?.(sender.data);
        if (allow === false) return;
        // Clear session storage before complete (security)
        if (clearDraftOnComplete) clearOwnDraftFields();
        setTimeout(() => onComplete?.(data), 200);
      });

      if (autoAdvanceEnabled) {
        model.onValueChanged.add((sender, options) => {
          if (
            options.value !== undefined &&
            options.value !== null &&
            options.value !== ""
          ) {
            if (!sender.currentPage.hasErrors(true, true)) {
              if (sender.currentPageNo < sender.pages.length - 1) {
                setTimeout(() => {
                  sender.nextPage();
                }, 500);
              }
            }
          }
        });
      }

      if (disableCompleteUntilCurrentPageComplete) {
        model.onValueChanged.add((sender) => updateCompleteButtonEnabled(sender));
        model.onAfterRenderSurvey.add((sender) => updateCompleteButtonEnabled(sender));
        model.onAfterRenderPage.add((sender) => updateCompleteButtonEnabled(sender));
        updateCompleteButtonEnabled(model);
      }

      if (props.surveyProps?.showPreviewBeforeComplete) {
        model.showPreviewBeforeComplete = true;
      }
      modelRef.current = model;
    }

    // Hide loader after render
    useEffect(() => {
      const timer = setTimeout(() => {
        const loader = document.getElementById(loaderWrapperId);
        if (loader) loader.style.display = "none";
      }, 500);
      return () => clearTimeout(timer);
    }, [loaderWrapperId]);

    // Unmount cleanup
    useEffect(() => {
      return () => {
        modelRef.current = null;
      };
    }, []);

    // Session Storage Logic
    // 1. Build the session draft key
    const sessionDraftKey = useMemo(() => buildDraftKey(getDraftUserId(user)), [user]);
    // Store in ref for access in callbacks
    sessionDraftKeyRef.current = sessionDraftKey;

    // 2. Check for token in URL - only run token prefill if token exists
    const urlToken = disableTokenPrefill
      ? undefined
      : new URLSearchParams(window.location.search).get("tk")?.trim();
    const tokenPrefill = useTokenPrefillHook(
      urlToken,
      urlToken ? getPrefillDataFromToken : undefined
    );

    // 3. Restore token -> latest of session/api, then start saver
    const { restored } = useSurveyRestoreHook(modelRef.current!, {
      storageKey: sessionDraftKey,
      initialPayload: initialData,
      tokenPrefill,
    });

    useEffect(() => {
      if (modelRef.current) {
        updateCompleteButtonEnabled(modelRef.current);
      }
    }, [restored, updateCompleteButtonEnabled]);

    useEffect(() => {
      const container = surveyContainerRef.current;
      if (!container) return;

      const scrollToFirstError = () => {
        const firstError =
          container.querySelector<HTMLElement>(".sd-question--error, .sd-element--with-error, .sd-question__erbox, [aria-invalid='true']");
        const target = firstError?.closest<HTMLElement>(".sd-question") ?? firstError;

        target?.scrollIntoView({ behavior: "smooth", block: "center" });
        target?.querySelector<HTMLElement>("input, select, textarea, button, [tabindex]:not([tabindex='-1'])")?.focus({ preventScroll: true });
      };

      const handleClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement | null;
        if (!target?.closest("#sv-nav-complete, .sd-navigation__complete-btn")) return;
        window.setTimeout(scrollToFirstError, 120);
      };

      container.addEventListener("click", handleClick, true);
      return () => container.removeEventListener("click", handleClick, true);
    }, [surveyContainerId]);

    // 4. Start saver (input-change only)
    useSurveySessionSaverHook(modelRef.current!, sessionDraftKey);

    // 5. Funnel "start" callback - fires once per page, on that page's first value
    // change (not on mount/page-transition). Single-page forms (most funnel steps) just
    // fire once; multi-page forms (e.g. the old Lander.tsx survey) fire once per page,
    // passing the page name so the caller can tell which page started.
    useEffect(() => {
      if (!onFirstInteraction) return;
      const model = modelRef.current!;
      const startedPageIdxs = new Set<number>();
      const handleValueChanged = () => {
        const pageIdx = model.currentPage ? model.pages.indexOf(model.currentPage) : 0;
        if (startedPageIdxs.has(pageIdx)) return;
        startedPageIdxs.add(pageIdx);
        onFirstInteraction(model.currentPage?.name ?? "");
      };
      model.onValueChanged.add(handleValueChanged);
      return () => model.onValueChanged.remove(handleValueChanged);
    }, [onFirstInteraction]);

    // 6. Optional: apply initialData from parent only if restore produced no values
    useEffect(() => {
      if (modelRef.current && initialData) {
        const hasAny = Object.keys(modelRef.current.data ?? {}).length > 0;
        if (hasAny) return;

        const flattenedData = getFlattenedData(initialData ?? {});

        if (flattenedData && Object.keys(flattenedData).length > 0) {
          modelRef.current.data = flattenedData;
          modelRef.current.render();
        }
      }
    }, [initialData]);

    // 7. Auto-skip AFTER restore
    useEffect(() => {
      if (!autoskip || !modelRef.current) return;

      const t = setTimeout(() => {
        const model = modelRef.current!;
        const isPageComplete = (pageIndex: number) => {
          const originalIndex = model.currentPageNo;
          model.currentPageNo = pageIndex;
          const isComplete = !model.currentPage.hasErrors(true, true);
          model.currentPageNo = originalIndex;

          return isComplete;
        };

        if (surveyProps.questionsOnPageMode === "questionPerPage") {
          let errorFound = false;

          for (let i = 0; i < model.pages.length; i++) {
            for (let q = 0; q < model.pages[i].questions.length; q++) {
              if (!model.pages[i].questions[q].hasErrors(true, true)) {
                model.nextPage();
              } else {
                errorFound = true;
                break;
              }
            }
            if (errorFound) break;
          }
        } else {
          const totalPages = model.pages.length;
          let pagesChecked = 0;

          for (let i = 0; i < totalPages; i++) {
            if (!isPageComplete(i)) break;

            if (pagesChecked === totalPages - 1) {
              model.showPreviewBeforeComplete = true;
              model.previewMode = "answeredQuestions";
              model.showPreview();
            }
            pagesChecked = pagesChecked + 1;
            model.nextPage();
          }
        }
      }, 300);

      return () => clearTimeout(t);
    }, [autoskip, restored, surveyProps.questionsOnPageMode]);

    return (
      <>
        <div
          id={surveyContainerId}
          style={surveyContainerStyle}
          className={`sd-root-modern position-relative ${wrapperFormCssClass ?? ""
            }`}
          ref={surveyContainerRef}
        >
          <Survey model={modelRef.current!} />
        </div>
        {!suppressLoader && (
          <div id={loaderWrapperId} style={{ display: "block" }}>
            <Loader text={loadingMessage} />
          </div>
        )}
      </>
    );
  }
);
